import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePayment } from '@/lib/api';
import type { InvoicePayment } from '@/types';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Trash2 } from 'lucide-react';

interface PaymentHistoryProps {
  payments: InvoicePayment[];
  invoiceId: string;
  invoiceStatus: string;
}

export function PaymentHistory({
  payments,
  invoiceId,
  invoiceStatus,
}: PaymentHistoryProps) {
  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<InvoicePayment | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canDelete = invoiceStatus !== 'void' && invoiceStatus !== 'paid';

  const deleteMutation = useMutation({
    mutationFn: (paymentId: string) => deletePayment(invoiceId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      setDeleteTarget(null);
      setError(null);
    },
    onError: (err: Error) => {
      setError(err.message || 'Failed to delete payment');
    },
  });

  if (!payments || payments.length === 0) {
    return (
      <div className="rounded-lg border border-white/10 bg-bg-tertiary p-6 text-center text-text-secondary">
        No payments recorded
      </div>
    );
  }

  return (
    <>
      <div className="rounded-lg border border-white/10 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Recorded By</TableHead>
              {canDelete && <TableHead className="w-10" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((payment) => (
              <TableRow key={payment.id}>
                <TableCell>{formatDate(payment.paymentDate)}</TableCell>
                <TableCell className="font-medium">
                  {formatCurrency(payment.amount)}
                </TableCell>
                <TableCell>{payment.paymentMethod}</TableCell>
                <TableCell className="text-text-secondary">
                  {payment.reference || '—'}
                </TableCell>
                <TableCell className="text-text-secondary">
                  {payment.createdBy
                    ? `${payment.createdBy.firstName} ${payment.createdBy.lastName}`
                    : '—'}
                </TableCell>
                {canDelete && (
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-text-secondary hover:text-red-400"
                      onClick={() => setDeleteTarget(payment)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setError(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Trash2 className="h-5 w-5" />
              Delete Payment
            </DialogTitle>
            <DialogDescription>
              Remove this {formatCurrency(deleteTarget?.amount ?? 0)} payment
              recorded on{' '}
              {deleteTarget ? formatDate(deleteTarget.paymentDate) : ''}? This
              action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          {error && <p className="text-sm text-red-400 py-2">{error}</p>}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
